// TaskListV2 — re-styled task list for the V3 "Execução ativa" right rail.
//
// Takes a `TodoList` (the same type the existing v1 TaskList consumes from
// `lastToolEvent.eventData`) and renders rows in the v2 design language:
//   - Done   → green-check tile, label strikethrough
//   - Active → amber spinner tile, amber-left-border row, bg2 background
//   - Pending → faint outline tile, dimmed label
//
// Pure presentational. The host wires it to wherever the todo list lives
// (typically by listening to `lastToolEvent.eventName === 'todolistUpdated'`).

import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { TodoItem, TodoList, TodoItemStatus } from '../../models/todoListModel';
import { CheckIcon } from './icons';

const spinKeyframes = `@keyframes v2tspin{to{transform:rotate(360deg)}}`;

function normalizeStatus(s: TodoItemStatus): 'completed' | 'in_progress' | 'failed' | 'pending' {
    const v = String(s).toLowerCase();
    if (v === 'completed') return 'completed';
    if (v === 'inprogress' || v === 'in_progress') return 'in_progress';
    if (v === 'failed') return 'failed';
    return 'pending';
}

function TaskDot({ status }: { status: 'completed' | 'in_progress' | 'failed' | 'pending' }) {
    if (status === 'completed') {
        return (
            <Box
                sx={(theme) => ({
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.success.main,
                    color: theme.palette.background.default,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: '2px',
                })}
            >
                <CheckIcon size={11} />
            </Box>
        );
    }
    if (status === 'in_progress') {
        // Squared rounded ring with one transparent side — matches the v2
        // design's "active" indicator (rotating partial border, gap on
        // the right edge so the spin reads).
        return (
            <Box
                sx={(theme) => ({
                    width: 18,
                    height: 18,
                    borderRadius: '5px',
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRightColor: 'transparent',
                    animation: 'v2tspin 1s linear infinite',
                    flexShrink: 0,
                    mt: '1px',
                })}
            />
        );
    }
    if (status === 'failed') {
        return (
            <Box
                sx={(theme) => ({
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.background.default,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: '2px',
                    fontSize: 11,
                    fontWeight: 700,
                })}
            >
                !
            </Box>
        );
    }
    return (
        <Box
            sx={(theme) => ({
                width: 16,
                height: 16,
                borderRadius: '50%',
                border: `1.5px solid ${theme.palette.text.disabled}`,
                flexShrink: 0,
                mt: '2px',
            })}
        />
    );
}

export interface TaskListV2Props {
    todoList: TodoList | null | undefined;
    /** Compact mode: smaller paddings. */
    dense?: boolean;
}

export const TaskListV2: React.FC<TaskListV2Props> = ({ todoList, dense }) => {
    const currentRef = useRef<HTMLDivElement | null>(null);

    // Pin the active task into view as the agent advances through the
    // checklist. We delegate to `scrollIntoView` so the closest
    // scrollable ancestor (the panel body in v2) handles the actual
    // scroll — this keeps the task list itself overflow-free.
    useEffect(() => {
        const el = currentRef.current;
        if (!el) return;
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [todoList?.currentWorkingItemKey]);

    if (!todoList || !todoList.items || todoList.items.length === 0) return null;

    const currentKey = todoList.currentWorkingItemKey;

    return (
        <>
            <style>{spinKeyframes}</style>
            <Box sx={{ display: 'flex', flexDirection: 'column',overflow:'scroll' }}>
                {todoList.items.map((item: TodoItem) => {
                    const status = normalizeStatus(item.status);
                    const isCurrent = item.key === currentKey;
                    return (
                        <Box
                            key={item.key}
                            ref={isCurrent ? currentRef : undefined}
                            sx={(theme) => ({
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.25,
                                px: 1,
                                py: dense ? 0.75 : 1,

                                borderRadius: `${theme.shape.radiusSm}px`,
                                // Active task highlight is purely text-based per the
                                // v2 design — no left border, no background fill.
                                mb: 0.25,
                            })}
                        >
                            <TaskDot status={status} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={(theme) => ({
                                        fontSize: 12,
                                        lineHeight: 1.4,
                                        fontWeight: isCurrent && status === 'in_progress' ? 500 : 400,
                                        color: status === 'pending'
                                            ? theme.palette.text.disabled
                                            : isCurrent && status === 'in_progress'
                                                ? theme.palette.primary.main
                                                : theme.palette.text.primary,
                                        textDecoration: status === 'completed' ? 'line-through' : 'none',
                                        // Match the completed TaskDot's success green so the
                                        // strikethrough reads as "this is done" rather than as
                                        // a faint smudge. Default `text.disabled` (#5a5a62)
                                        // sat too close to the surrounding text on the dark
                                        // surface and the line was nearly invisible.
                                        textDecorationColor: status === 'completed'
                                            ? theme.palette.success.main
                                            : theme.palette.text.disabled,
                                        // Long task labels wrap inside the panel's narrow column;
                                        // without this they pick up the document's default
                                        // text-align and look justified (last line spacing reads
                                        // off). Pin to left so wrapped labels stay flush.
                                        textAlign: 'left',
                                    })}
                                >
                                    {item.label}
                                </Typography>
                                {item.detail && (
                                    <Typography
                                        sx={(theme) => ({
                                            fontSize: 10.5,
                                            lineHeight: 1.4,
                                            mt: 0.4,
                                            color: status === 'in_progress' ? theme.palette.text.secondary : theme.palette.text.disabled,
                                            fontFamily: theme.typography.fontFamilyMono,
                                            wordBreak: 'break-word',
                                            textAlign: 'left',
                                        })}
                                    >
                                        {item.detail}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </>
    );
};

TaskListV2.displayName = 'TaskListV2';
